// ============================================================
// CastBack API Client
// Phase 28A — settings + library endpoints
// Phase 28C — alert types + alert list/detail endpoints
// Phase 28D — native upload + processing status endpoints
// Phase 28E — action/dismiss, stats endpoints
// ============================================================

import { apiFetch, getAuthHeader } from './api'
import { config } from './config'

// ---- Types ----

export interface CastbackSettings {
  user_id: string
  enabled: boolean
  signal_threshold: number
  include_phases: string[]
  cooldown_days: number
  min_content_age_days: number
  min_views_pct: number
  evergreen_only: boolean
  match_method: string
  embed_indexing_enabled: boolean
  notify_in_app: boolean
  notify_push: boolean
  created_at: string
  updated_at: string
}

export interface CastbackContentItem {
  id: string
  user_id: string
  content_id: string | null
  native_upload_id: string | null
  platform: string
  title: string | null
  permalink: string | null
  thumbnail_url: string | null
  thumbnail_source: string | null
  published_at: string | null
  views_snapshot: number
  follower_count_snapshot: number
  topic_tags: string[]
  raw_terms: string[]
  is_evergreen: boolean
  target_platforms: string[]
  platform_detection: string
  index_version: number
  indexed_at: string
  last_updated: string
}

export interface CastbackLibraryResponse {
  items: CastbackContentItem[]
  total: number
  limit: number
  offset: number
}

export interface CastbackLibraryStats {
  total_indexed: number
  total_eligible: number
  platform_counts: Record<string, number>
  evergreen_count: number
  last_indexed_at: string | null
  top_topics: Array<{ topic: string; count: number }>
}

// ---- Settings ----

export function getCastbackSettings(): Promise<CastbackSettings> {
  return apiFetch('/castback/settings')
}

export function updateCastbackSettings(
  data: Partial<Omit<CastbackSettings, 'user_id' | 'created_at' | 'updated_at'>>,
): Promise<CastbackSettings> {
  return apiFetch('/castback/settings', {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

// ---- Library ----

export function getCastbackLibrary(params?: {
  platform?: string
  evergreen?: boolean
  limit?: number
  offset?: number
}): Promise<CastbackLibraryResponse> {
  const searchParams = new URLSearchParams()
  if (params?.platform) searchParams.set('platform', params.platform)
  if (params?.evergreen !== undefined) searchParams.set('evergreen', String(params.evergreen))
  if (params?.limit) searchParams.set('limit', String(params.limit))
  if (params?.offset) searchParams.set('offset', String(params.offset))

  const qs = searchParams.toString()
  return apiFetch(`/castback/library${qs ? `?${qs}` : ''}`)
}

export function getCastbackLibraryStats(): Promise<CastbackLibraryStats> {
  return apiFetch('/castback/library/stats')
}

// ---- Manual index trigger ----

export function triggerCastbackIndex(): Promise<{ queued: boolean }> {
  return apiFetch('/castback/index/trigger', { method: 'POST' })
}

// ---- Alert types (28C — BI-C5: corrected per approval) ----

export interface CastbackAlert {
  id: string
  user_id: string
  podcast_id: string
  topic: string
  topic_phase: string          // 'early_rise' | 'peak' | 'stable' | 'declining'
  topic_velocity: number | null
  signal_score: number
  matched_count: number
  top_content_ids: string[]
  match_scores: Record<string, number>
  status: string               // 'active' | 'actioned' | 'dismissed' | 'expired'
  actioned_type: string | null
  fired_at: string
  actioned_at: string | null
  dismissed_at: string | null
  expires_at: string | null
  cooldown_until: string | null
  created_at: string
  content?: AlertContentItem[] // enriched by server on list route
}

export interface AlertContentItem {
  id: string
  title: string | null
  platform: string
  thumbnail_url: string | null
  thumbnail_source: string | null
  views_snapshot: number
}

export interface MatchedContentItem {
  id: string
  title: string | null
  platform: string
  permalink: string | null
  thumbnail_url: string | null
  thumbnail_source: string | null
  views_snapshot: number
  match_score: number
}

export type AlertDetailResponse = CastbackAlert & {
  signal_breakdown: {
    gs_trending: { score: number; phase: string; velocity: number | null }
    signal_topics: { score: number }
  }
  matched_content: MatchedContentItem[]
}

export interface CastbackAlertListResponse {
  alerts: CastbackAlert[]
  total: number
  limit: number
  offset: number
}

// ---- Alert endpoints ----

export function getCastbackAlerts(params?: {
  status?: string
  limit?: number
  offset?: number
}): Promise<CastbackAlertListResponse> {
  const searchParams = new URLSearchParams()
  if (params?.status) searchParams.set('status', params.status)
  if (params?.limit) searchParams.set('limit', String(params.limit))
  if (params?.offset) searchParams.set('offset', String(params.offset))
  const qs = searchParams.toString()
  return apiFetch(`/castback/alerts${qs ? `?${qs}` : ''}`)
}

export function getCastbackAlertDetail(alertId: string): Promise<AlertDetailResponse> {
  return apiFetch(`/castback/alerts/${alertId}`)
}

// ---- Native upload types (28D) ----

export interface CastbackNativeUpload {
  id: string
  user_id: string
  original_filename: string
  file_size_bytes: number
  mime_type: string
  duration_seconds: number | null
  width: number | null
  height: number | null
  thumbnail_url: string | null
  target_platforms: string[]
  status: string // 'pending' | 'processing' | 'extracting' | 'transcribing' | 'indexing' | 'complete' | 'failed'
  error_message: string | null
  created_at: string
  updated_at: string
}

// BI-D7: Use raw fetch() — apiFetch hardcodes Content-Type: application/json which breaks FormData
export async function uploadCastbackVideo(
  file: File, title: string | null, targetPlatforms: string[],
): Promise<{ upload_id: string }> {
  const formData = new FormData()
  formData.append('video', file)
  if (title) formData.append('title', title)
  formData.append('target_platforms', JSON.stringify(targetPlatforms))

  const headers = await getAuthHeader() // Auth only — NO Content-Type (browser sets multipart boundary)

  const res = await fetch(`${config.apiBase}/castback/upload`, {
    method: 'POST',
    headers,
    body: formData,
  })

  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: res.statusText }))
    throw new Error(error.message || `Upload failed: ${res.status}`)
  }

  return res.json()
}

export function getCastbackUploadStatus(uploadId: string): Promise<CastbackNativeUpload> {
  return apiFetch(`/castback/upload/${uploadId}/status`)
}

// ---- Stats types (28E) ----

export interface CastbackStats {
  alerts_fired_30d: number
  actioned_count: number
  dismissed_count: number
  expired_count: number
  actioned_by_type: { boost: number; clip: number; episode: number; share: number }
}

// ---- Alert action endpoints (28E) ----

export function actionCastbackAlert(
  alertId: string, status: 'actioned' | 'dismissed', actionedType?: string,
): Promise<{ success: boolean; alert: CastbackAlert }> {
  return apiFetch(`/castback/alerts/${alertId}`, {
    method: 'PATCH',
    body: JSON.stringify({ status, actioned_type: actionedType }),
  })
}

export function deleteCastbackAlert(alertId: string): Promise<{ success: boolean }> {
  return apiFetch(`/castback/alerts/${alertId}`, { method: 'DELETE' })
}

export function getCastbackStats(): Promise<CastbackStats> {
  return apiFetch('/castback/stats')
}
